import { useEffect, useRef } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { captureBookingViewed } from '@/lib/analytics';
import { Check } from 'lucide-react';
import { consumeBookingToken, getBooking, type BookingView } from '@/api/generated';
import { StatusChip } from '@/components/StatusChip';
import { Eyebrow } from '@/components/ui/eyebrow';
import { buttonVariants } from '@/components/ui/button';
import { PriceBreakdown } from '@/components/PriceBreakdown';
import { formatMoney, fromGuestParty } from '@/lib/booking';
import { partyLabel } from '@/lib/party';
import { applySeo } from '@/lib/seo';
import { resolveExistingBookingCancellationLabel } from '@/lib/calendar';
import { m } from '@/paraglide/messages';

// Booking view page (/booking/$token).
// Renders a guest-safe confirmation/view of a booking. The token is a stateless
// HMAC-signed blob delivered by the booking confirmation email (4B).
//
// Privacy: the token must NOT leak via Referer to third-party origins (PostHog,
// PayPal, CDN). We inject a no-referrer meta tag and use a route-scoped title
// so the token never appears in analytics payloads.
export const Route = createFileRoute('/booking/$token')({
  component: BookingView,
});

function BookingView() {
  const { token } = Route.useParams();
  const queryClient = useQueryClient();

  // OH-159: the same emailed link now also signs the guest in — but only the
  // login half of the token is single-use (the view stays multi-use, 30
  // days, unchanged). A 401 here is an ORDINARY, EXPECTED outcome (the
  // guest's own second visit, a forwarded link, a booking with no guest to
  // sign in) — never surfaced as an error. This call is fire-and-forget and
  // must never affect the booking view rendered below: no toast, no banner,
  // no loading state tied to it.
  //
  // Keyed on the token itself (not a bare boolean) so a SPA navigation from
  // one emailed link to another still fires once per token, not once ever.
  // StrictMode double-invokes effects in dev (mount -> cleanup -> mount,
  // synchronously) — the ref persists across that, so the second invocation
  // sees its own token already recorded here and skips, exactly like
  // auth.magic.tsx's startedRef for the same hazard.
  const consumedTokenRef = useRef<string | null>(null);
  useEffect(() => {
    if (consumedTokenRef.current === token) return;
    consumedTokenRef.current = token;

    void (async () => {
      try {
        const { response } = await consumeBookingToken({ body: { token } });
        if (response?.status === 204) {
          // removeQueries, not invalidateQueries, mirroring login.tsx's and
          // auth.magic.tsx's own 204 handlers: this page has no mounted
          // ['guest-profile'] observer, so invalidating would only mark the
          // cache stale, not clear it. If that cache still holds the `null`
          // 401-sentinel from an earlier /cabinet visit this session, the
          // NEXT /cabinet mount would render with data === null on its first
          // pass and redirect to /login before a background refetch could
          // land — the exact bounce those two files' own comments describe.
          // ['my-bookings'] is cleared alongside it for the same reason
          // login.tsx clears both: a forwarded link must not surface the
          // previous guest's stays on a shared device.
          queryClient.removeQueries({ queryKey: ['guest-profile'] });
          queryClient.removeQueries({ queryKey: ['my-bookings'] });
        }
        // Any other outcome (401 most commonly, or a network/5xx failure) is
        // silent by design — see the comment above the ref.
      } catch {
        // Same treatment as a non-204 response: silent.
      }
    })();
  }, [token, queryClient]);

  // No-referrer: prevent the token from leaking via Referer header to any
  // third-party requests that fire on this page (PostHog, PayPal, fonts, etc.).
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'referrer');
    meta.setAttribute('content', 'no-referrer');
    meta.setAttribute('data-booking-view', '');
    document.head.appendChild(meta);
    return () => {
      document.head.querySelector('meta[data-booking-view]')?.remove();
    };
  }, []);

  // null = 404 (expired/invalid link); undefined = not yet fetched; BookingView = success.
  const { data, isLoading, isError } = useQuery<BookingView | null>({
    queryKey: ['booking-view', token],
    queryFn: async () => {
      const { data, error, response } = await getBooking({ path: { token } });
      if (response?.status === 404) {
        // Treat as a known "expired or invalid" state, not a fatal error.
        return null;
      }
      if (error || !data) {
        throw new Error('fetch_failed');
      }
      return data;
    },
    retry: false, // don't retry 404s
    staleTime: 5 * 60 * 1000, // 5 min — booking details rarely change
  });

  // OH-71: a guest reaching their booking from the emailed link is the last
  // step of the funnel and was never recorded. Fires once the booking really
  // loaded — not on mount, when it might still 404.
  useEffect(() => {
    if (data) captureBookingViewed('token_link');
  }, [data]);

  // Minimal SEO: title only, no og:url (would contain the token).
  // noindex: the token is a secret; never publish it in a canonical tag or
  // allow indexing. robots.txt Disallows /booking/ as belt-and-suspenders.
  useEffect(() => {
    const title = data?.apartment_title
      ? `Booking at ${data.apartment_title}`
      : 'Your Booking';
    return applySeo({ title, noindex: true });
  }, [data]);

  if (data) {
    return <BookingDetails booking={data} />;
  }

  // Non-success states share a simple centered shell.
  return (
    <main className="mx-auto w-full max-w-xl flex-1 px-6 py-16">
      <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-8 text-center shadow-[var(--shadow-sm)]">
        {isLoading ? (
          <p className="text-muted">Loading your booking…</p>
        ) : isError ? (
          <>
            <h1 className="font-serif text-2xl font-semibold tracking-tight text-[var(--ollio-ink)]">
              Something went wrong
            </h1>
            <p className="mt-2 text-muted">
              We could not load your booking. Please try again or contact support.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-serif text-2xl font-semibold tracking-tight text-[var(--ollio-ink)]">
              Link invalid or expired
            </h1>
            <p className="mt-2 text-muted">
              This booking link is no longer valid. Links are valid for 30 days after your
              booking was confirmed. Please contact us if you need assistance.
            </p>
          </>
        )}
        <BackLink className="mt-6" />
      </div>
    </main>
  );
}


export function BookingDetails({
  booking,
  justPaid = false,
}: {
  booking: BookingView;
  // OH-159: set ONLY by /confirmation, reached the moment payment succeeds —
  // the exact "might close this tab right now" instant. /booking/$token
  // reuses this same component (this export) for every OTHER visit to a
  // booking, including a guest's own later revisit and a second device
  // opening a forwarded link — where the login half of the token may already
  // be spent (401) for THIS visit, so a claim like "you're signed in" would
  // not always be true there. Default false keeps booking.$token's copy
  // exactly as it reads today, plus the one durable-carrier sentence below
  // that IS true regardless of this visit's login outcome: the view itself
  // is multi-use for 30 days no matter what.
  justPaid?: boolean;
}) {
  const isConfirmed = booking.status.toLowerCase() === 'confirmed';
  const total =
    booking.total_amount_minor != null && booking.currency
      ? formatMoney(booking.total_amount_minor, booking.currency)
      : null;
  // Composition recorded on the booking. Absent means none was recorded —
  // every pre-existing booking, and every OTA booking today — so nothing
  // renders below in that case, not an empty cell.
  const partyText = booking.guests ? partyLabel(fromGuestParty(booking.guests)) : null;

  // OH-68 §3.5: THIS booking's own cancellation-policy snapshot (not
  // today's canonical policy — see the API's own free_cancellation doc
  // comment), gated (via resolveExistingBookingCancellationLabel) on
  // status==='confirmed' AND the deadline itself still being in the future.
  // Review round 1, IMPORTANT 1: status alone isn't enough — nothing in
  // this codebase ever moves a booking OUT of 'confirmed' (no write path
  // sets checked_in/checked_out), so a stay that ended years ago is still
  // 'confirmed' today, and would otherwise render a deadline that already
  // lapsed. Also renders nothing for an empty/unreadable snapshot (every
  // pre-OH-68 booking) or one with no 100%-refund tier — never a guessed or
  // stale deadline.
  const cancellationDeadlineLabel = resolveExistingBookingCancellationLabel(
    booking.status,
    booking.free_cancellation,
  );

  return (
    <main className="flex-1">
      {/* Status-aware navy hero — celebratory only when confirmed. */}
      <section className="bg-[var(--ollio-navy)] px-6 pb-28 pt-14 text-center text-white sm:pb-32 sm:pt-16">
        {isConfirmed ? (
          <div className="mx-auto mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-full border-2 border-[var(--ollio-success)] bg-[rgba(47,125,82,0.22)]">
            <Check className="h-[34px] w-[34px] text-[#7fd6a0]" aria-hidden="true" />
          </div>
        ) : null}
        <Eyebrow onDark className="mb-3.5">
          {isConfirmed ? 'Reservation confirmed' : 'Your reservation'}
        </Eyebrow>
        <h1 className="mx-auto max-w-2xl font-serif text-3xl font-semibold leading-[1.08] tracking-tight sm:text-5xl">
          {isConfirmed ? `You're staying at ${booking.apartment_title}.` : booking.apartment_title}
        </h1>
        {booking.guest_email ? (
          <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-white/80 sm:text-base">
            We've emailed your {isConfirmed ? 'check-in' : 'booking'} details to{' '}
            <span className="font-semibold text-white">{booking.guest_email}</span>.{' '}
            {/* OH-159: the email is now the durable way back to this booking,
                not just a copy of it — see the `justPaid` doc comment above
                for why the wording differs on /confirmation. */}
            {justPaid
              ? 'You can close this tab now — that email is how you get back to this booking.'
              : 'That email is also how you get back to this booking.'}
            {isConfirmed ? " Everything's ready for you in Belgrade." : ''}
          </p>
        ) : null}
      </section>

      {/* Itinerary card overlapping the hero. */}
      <div className="mx-auto -mt-20 max-w-2xl px-6">
        <div className="overflow-hidden rounded-[var(--radius-2xl)] border border-[var(--border-hairline)] bg-card shadow-[var(--shadow-xl)]">
          <div className="flex items-center gap-3 border-b border-[var(--border-soft)] px-6 py-4">
            <StatusChip status={booking.status} />
            <span className="text-xs font-bold uppercase tracking-[0.08em] text-[var(--ollio-gold-deep)]">
              Your itinerary
            </span>
          </div>

          {/* The Guests cell makes this five cells, and five divides evenly
              into neither 2 nor 4 columns: the leftover tracks would show as
              a solid block of the divider colour, reading as a rendering bug
              on the page every guest lands on after paying. Total spans the
              remainder instead, so the last row is always full — with or
              without the composition. */}
          <dl className="grid grid-cols-2 gap-px bg-[var(--border-soft)] sm:grid-cols-4">
            <GridCell label="Check-in" value={booking.checkin_date} />
            <GridCell label="Check-out" value={booking.checkout_date} />
            {partyText ? <GridCell label="Guests" value={partyText} testId="guest-party" /> : null}
            <GridCell label="Nights" value={String(booking.nights)} />
            <GridCell
              label="Total"
              value={total ?? '—'}
              mono
              className={partyText ? 'col-span-2 sm:col-span-2' : undefined}
            />
          </dl>

          {booking.ota_reservation_code ? (
            <p className="px-6 py-4 text-sm text-muted">
              Reservation code:{' '}
              <span className="font-mono text-[var(--ollio-ink)]">
                {booking.ota_reservation_code}
              </span>
            </p>
          ) : null}

          {/* Below the grid; the grid's own Total cell (above) stays as-is.
              Rendered only when the booking carries a breakdown (OTA, manual
              and pre-migration bookings never do) so an absent breakdown
              degrades to exactly today's rendering — no empty block. */}
          {booking.price_breakdown ? (
            <div className="border-t border-[var(--border-soft)] px-6 py-4">
              <PriceBreakdown
                lineItems={booking.price_breakdown.line_items}
                nights={booking.price_breakdown.nights}
                totalMinor={booking.price_breakdown.total_amount_minor}
                currency={booking.price_breakdown.currency}
              />
            </div>
          ) : null}

          {cancellationDeadlineLabel ? (
            <p className="border-t border-[var(--border-soft)] px-6 py-4 text-sm text-muted">
              {m.cancellation_deadline({ date: cancellationDeadlineLabel })}
            </p>
          ) : null}
        </div>

        <div className="mt-8 text-center">
          <BackLink />
        </div>
      </div>
    </main>
  );
}

function GridCell({
  label,
  value,
  mono = false,
  testId,
  className,
}: {
  label: string;
  value: string;
  mono?: boolean;
  testId?: string;
  className?: string;
}) {
  return (
    <div className={`bg-card px-5 py-4 ${className ?? ''}`} data-testid={testId}>
      <dt className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--ollio-gold-deep)]">
        {label}
      </dt>
      <dd
        className={`text-lg font-semibold text-[var(--ollio-ink)] ${mono ? 'font-mono tabular-nums' : ''}`}
      >
        {value}
      </dd>
    </div>
  );
}

function BackLink({ className = '' }: { className?: string }) {
  return (
    <Link to="/" className={`${buttonVariants({ variant: 'secondary', pill: true })} ${className}`}>
      Back to apartments
    </Link>
  );
}
